import React, { useMemo } from 'react'
import { Form } from '@carsys/form'
import { useTranslation } from 'react-i18next'
import { FormValues } from './types.d'
import * as Yup from 'yup'
import { useUnauthorizedUserThemeContext } from 'app/auth/components/UnauthorizedUserThemeContextProvider'
import { BaseStepLayout } from '../BaseStepLayout'
import { defaultStepFormProps } from '../types.d'
import { StepNavigation } from '../StepNavigation'
import { OnboardingState } from 'app/auth/components/hooks/use-onboarding-setup'
import { hasPermission } from 'utils/Subscription/RequiredPermissions'
import { PERMISSION_ONLY_PORTAL } from 'utils/Subscription/PermissionComposites'
import { WithRunInBackgroundHelper } from '../use-should-run-in-background'

export const OnboardingWelcomeMessageForm: React.FC<
  defaultStepFormProps<FormValues> & { onSubmit: (v: FormValues) => void; onboardingInfo: OnboardingState }
> = ({ onSubmit, prevStep, isFirstStep, onboardingInfo, isPersisting, onSubmitSuccess, hideSkip, shouldRunInBackground, initialData }) => {
  const { t } = useTranslation()
  const initialValues = useMemo(() => ({ IsWelcomeMessageSeen: true, ...initialData } as FormValues), [])
  const { applicationName: appName, environmentName: envName } = useUnauthorizedUserThemeContext()

  const validationSchema = Yup.object().shape({
    IsWelcomeMessageSeen: Yup.boolean().oneOf([true])
  })

  const formSubmit = async (v: FormValues) => {
    onSubmit(v)
    return v
  }

  const subTitles = useMemo(() => {
    const isPortal = hasPermission(onboardingInfo.modules, PERMISSION_ONLY_PORTAL, {})
    if (isPortal) {
      return [t('onboarding.welcome_message.welcome_message_step_info_second_portal', { appName })]
    }
    return [t('onboarding.welcome_message.welcome_message_step_info_second', { appName })]
  }, [onboardingInfo.modules, t, envName, appName])

  return (
    <BaseStepLayout title={t('onboarding.welcome_message.welcome_message_header')} subTitles={subTitles}>
      <Form
        onSubmit={formSubmit}
        onSubmitSuccess={onSubmitSuccess}
        initialValues={initialValues}
        NavigationConfirmation={null}
        validationSchema={validationSchema}
      >
        {({ isSubmitting, handleSubmit }) => (
          <WithRunInBackgroundHelper isSubmitting={isSubmitting} onSubmit={handleSubmit} enabled={shouldRunInBackground}>
            <StepNavigation prevStep={prevStep} hideBack={isFirstStep} hideSkip={hideSkip} isLoading={isPersisting || isSubmitting} />
          </WithRunInBackgroundHelper>
        )}
      </Form>
    </BaseStepLayout>
  )
}

export default OnboardingWelcomeMessageForm
